import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerDocuments, uploadCustomerDocument, deleteCustomerDocument } from "../../../redux/customer/customerSlice";
import { ReactComponent as DocumentIcon } from "../../../assets/svgs/dynamicDocument.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/deleteIcon.svg";
import { ReactComponent as EyeIcon } from "../../../assets/svgs/greenEye.svg";
import CardsBorder from "../../../components/Cards/CardsBorder";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import AddCustomerDocument from "./AddCustomerDocument";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const CustomerDocument = ({ clientId }) => {

  const dispatch = useDispatch();
  const { customerDocuments } = useSelector((state) => state.customer);
  const [addDocument, setAddDocument] = useState(false);
  const [popup, setPopup] = useState({
    visible: false,
    type: "success",
    title: "",
    message: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    docId: null,
    docName: "",
  });

  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  useEffect(() => {
    if (clientId) {
      dispatch(fetchCustomerDocuments(clientId));
    }
  }, [dispatch, clientId]);

  const handleSaveDocument = async ({ fileName, file }) => {
    if (!clientId) return;
    const formData = new FormData();
    formData.append("documents", file);
    formData.append("fileName", fileName);

    const resultAction = await dispatch(uploadCustomerDocument({ id: clientId, data: formData }));
    if (uploadCustomerDocument.fulfilled.match(resultAction)) {
      showPopup("success", "Success", "Document uploaded successfully");
      setAddDocument(false);
      dispatch(fetchCustomerDocuments(clientId));
    } else {
      showPopup("error", "Error", resultAction.payload || "Failed to upload document");
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!clientId) return;
    const resultAction = await dispatch(deleteCustomerDocument({ id: clientId, docId }));
    if (deleteCustomerDocument.fulfilled.match(resultAction)) {
      showPopup("success", "Success", "Document removed successfully");
      setDeleteConfirmation({ visible: false, docId: null, docName: "" });
    } else {
      showPopup("error", "Error", resultAction.payload || "Failed to delete document");
    }
  };

  return (
    <div
      style={{ paddingRight: "40px", paddingLeft: "40px", paddingTop: "20px" }}
    >
      <CardsBorder>
        <div className="mx-20">
          <div>
            <div className="flex justify-between items-center my-2">
              <p className="text-xl">Document</p>
              <CustomButton
                buttonType="primary"
                variant="ok"
                buttonText="+ ADD NEW"
                className={"py-6 text-xs font-medium"}
                onClick={() => setAddDocument(true)}
              />
            </div>
            {customerDocuments && customerDocuments.length > 0 ? (
              customerDocuments.map((doc, index) => (
                <div
                  key={index}
                  className="w-full  border border-[#efefef] rounded-[10px]  mb-4"
                >
                  <div className="flex justify-between mx-10 my-4">
                    <div className="flex gap-6">
                      <DocumentIcon />
                      <div>
                        <div className="text-[17px] font-light">
                          {doc.fileName || "Document"}
                        </div>
                        <div className="text-[#929292] text-sm mt-1">
                          {doc.size ? `${(doc.size / 1024).toFixed(2)} kb` : "Uploaded"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 pt-2">
                      <button
                        onClick={() => {
                          if (doc.files && doc.files.length > 0) {
                            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8081";
                            const filePath = doc.files[0].filePath;
                            const fullUrl = filePath.startsWith("http") ? filePath : `${baseUrl}${filePath}`;
                            window.open(fullUrl, "_blank");
                          } else {
                            showPopup("error", "Error", "File path not found");
                          }
                        }}
                        className="group relative flex items-center gap-2 px-5 py-2 overflow-hidden bg-white text-[#06513D] text-[10px] font-bold rounded-xl transition-all duration-300 hover:text-white border border-[#06513D]/20 uppercase tracking-widest shadow-sm active:scale-95"
                      >
                        <div className="absolute inset-0 w-0 bg-[#06513D] transition-all duration-300 ease-out group-hover:w-full" />
                        <span className="relative flex items-center gap-2">
                          <EyeIcon className="w-3.5 h-3.5 fill-current transition-transform duration-500 group-hover:rotate-[360deg]" />
                          <span>View Document</span>
                        </span>
                      </button>
                      <div
                        className="cursor-pointer p-2 rounded-full hover:bg-red-50 transition-all duration-300 active:scale-90 group"
                        onClick={() => {
                          setDeleteConfirmation({
                            visible: true,
                            docId: doc._id,
                            docName: doc.fileName || "Document",
                          });
                        }}
                      >
                        <DeleteIcon className="transition-transform duration-300 group-hover:scale-110" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-[#929292] mt-4 mb-4 text-center">No documents available.</p>
            )}
          </div>
        </div>

      </CardsBorder>
      {addDocument && (
        <AddCustomerDocument
          onOpen={addDocument}
          onClose={() => setAddDocument(false)}
          onSave={handleSaveDocument}
        />
      )}
      {popup.visible && (
        <div className="fixed inset-0  bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}

      {deleteConfirmation.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="warning"
            title="Confirm Delete"
            message={`Are you sure you want to delete ${deleteConfirmation.docName}?`}
            showActions={true}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onCancel={() =>
              setDeleteConfirmation({
                visible: false,
                docId: null,
                docName: "",
              })
            }
            onConfirm={() => handleDeleteDocument(deleteConfirmation.docId)}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerDocument;
